import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import Box from '@opendoor/bricks/core/Box';
import { novo } from '@opendoor/bricks/theme';

import { EiChevronDownIcon } from '../../../shared/EiChevronDownIcon';
import type { MegaNavItem, MenuLink } from '../types';
import MegaNavFlyout from './MegaNavFlyout';
import { NavLink } from './NavLink';

const HEADER_HEIGHT = '72px';

interface MegaNavProps {
  slug: string;
  mainNavLinks: MenuLink[];
  megaNavData?: MegaNavItem[];
}

export const MegaNav: React.FC<MegaNavProps> = ({ slug, megaNavData }) => {
  const [openNavItem, setOpenNavItem] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (itemName: string) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpenNavItem(itemName);
  };

  const handleMouseLeave = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setOpenNavItem(null);
      closeTimerRef.current = null;
    }, 400);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenNavItem(null);
      }
    };

    if (openNavItem) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openNavItem]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  return (
    <Box ref={navRef} gridColumn="1 / -1" gridRow="1 / 2" position="relative">
      <Box
        as="ul"
        m="0"
        p="0"
        display={['none', null, 'flex']}
        justifyContent="center"
        analyticsName="cosmos-mega-nav-menu-list"
        css={css`
          list-style: none;
        `}
      >
        {megaNavData?.map((navItem) => {
          const isOpen = openNavItem === navItem.name;
          const hasSections = navItem.sections && navItem.sections.length > 0;

          return (
            <Box
              as="li"
              key={navItem.name}
              position="relative"
              onMouseEnter={hasSections ? () => handleMouseEnter(navItem.name) : undefined}
              onMouseLeave={hasSections ? handleMouseLeave : undefined}
            >
              {hasSections ? (
                <>
                  <MegaNavLink
                    active={slug === navItem.href}
                    href={navItem.href}
                    name={navItem.name}
                    isOpen={isOpen}
                  >
                    {navItem.name}
                  </MegaNavLink>
                  <MegaNavFlyout
                    item={navItem}
                    isVisible={isOpen}
                    onMouseEnter={() => handleMouseEnter(navItem.name)}
                    onMouseLeave={handleMouseLeave}
                  />
                </>
              ) : (
                <NavLink active={slug === navItem.href} href={navItem.href} name={navItem.name}>
                  {navItem.name}
                </NavLink>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const MegaNavLink: React.FC<
  PropsWithChildren<{
    href: string;
    active?: boolean;
    name: string;
    isOpen: boolean;
  }>
> = ({ children, name, active, isOpen, href }) => (
  <Box
    as="a"
    href={href}
    display="inline-flex"
    alignItems="center"
    lineHeight="100"
    fontWeight="medium"
    py={3}
    px={5}
    h={HEADER_HEIGHT}
    aria-label="View link"
    aria-current={active}
    aria-expanded={isOpen}
    analyticsName={`cosmos-mega-nav-v2-nav-link-${name}`}
    css={css`
      background: transparent;
      border: none;
      cursor: pointer;
      color: ${novo.colors.warmgrey950};
      text-decoration: none;
      gap: 8px;

      &:hover,
      &:focus {
        color: ${novo.colors.brandBlue600};
        outline: 0;
      }
      &:active {
        color: ${novo.colors.brandBlue600};
      }
    `}
  >
    {children}
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width="0.6em"
      height="0.6em"
      css={css`
        transition: transform 0.2s ease;
        transform: ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
      `}
    >
      <EiChevronDownIcon />
    </Box>
  </Box>
);
